/**
 * Rejoining a deaf-to-deaf group call after reloading its room.
 *
 * Leaving a group call (hangup, closing the tab, reloading the room page)
 * records the member as left on the server, and the room refuses them until
 * they accept the call again: p2p_accept { callId, roomName, callerId }, which
 * the server answers with p2p_join_room, or an error such as CALL_FULL. The
 * page sends call_end on pagehide (endClientCallOnPageHide in the middleware)
 * and, in a group room, leaves this tab a marker (rememberGroupRejoin) so the
 * reloaded page can ask to come back in before it joins the conference. The
 * marker is tab-scoped (sessionStorage): closing the tab is leaving, and
 * nothing should bring that back. (The retired room pill, vrs-room-controls.js,
 * wrote it until 2026-09-24.)
 */
/** sessionStorage, web only: { callId, roomName, callerId, leftAt }. */
export const GROUP_REJOIN_KEY = 'vrs_group_rejoin';
/** A reload is seconds; anything older is not a reload. */
export const GROUP_REJOIN_MAX_AGE_MS = 10 * 60 * 1000;
/** How long the room waits for the server to seat it again. */
export const GROUP_REJOIN_TIMEOUT_MS = 10000;
// Answers to p2p_accept that mean "not coming back in". They carry no call id,
// so any of them while a rejoin is pending is taken as its answer.
// JOIN_DECLINED / JOIN_NO_ANSWER: the host said no, or did not answer
// (vrs-server/ws/group-join-approval.js).
const REJOIN_REFUSALS = ['CALL_ENDED', 'CALL_FULL', 'FORBIDDEN', 'INTERNAL_ERROR', 'INVITE_UNAVAILABLE', 'JOIN_DECLINED',
    'JOIN_NO_ANSWER', 'NOT_FOUND'];
/** How much longer than the host's answer window the wait lasts (the server times it). */
const HOST_ANSWER_MARGIN_MS = 5000;
let pendingRejoinCallId;
/**
 * Reads a marker left for this room, if it is recent and well formed.
 *
 * @param {string|null} raw - The stored value.
 * @param {string} roomName - The room this page is joining.
 * @param {number} now - The current time.
 * @returns {IGroupRejoin|null}
 */
export function parseGroupRejoin(raw, roomName, now = Date.now()) {
    try {
        const value = JSON.parse(raw || 'null');
        if (!value || typeof value !== 'object'
            || typeof value.callId !== 'string' || !value.callId
            || typeof value.callerId !== 'string' || !value.callerId
            || value.roomName !== roomName || !roomName.startsWith('group-')
            || typeof value.leftAt !== 'number' || now - value.leftAt > GROUP_REJOIN_MAX_AGE_MS || value.leftAt > now) {
            return null;
        }
        return { callId: value.callId, callerId: value.callerId, leftAt: value.leftAt, roomName };
    }
    catch {
        return null;
    }
}
function sessionStore() {
    try {
        // Web only; native has no tab to reload.
        return globalThis.sessionStorage;
    }
    catch {
        return undefined;
    }
}
/**
 * The marker for this room, if this tab just left it.
 *
 * @param {string} roomName - The room this page is joining.
 * @returns {IGroupRejoin|null}
 */
export function findGroupRejoin(roomName) {
    const store = sessionStore();
    return store ? parseGroupRejoin(store.getItem(GROUP_REJOIN_KEY), roomName) : null;
}
/**
 * Leave this tab the marker on its way out of a group room, so a reload asks
 * to be seated again. Only a group room whose stored call names the caller.
 *
 * @param {Object|null} call - The stored call being left.
 * @param {number} now - The current time.
 * @returns {void}
 */
export function rememberGroupRejoin(call, now = Date.now()) {
    if (!call || typeof call.callId !== 'string' || !call.callId
        || typeof call.clientId !== 'string' || !call.clientId
        || typeof call.roomName !== 'string' || !call.roomName.startsWith('group-')) {
        return;
    }
    try {
        sessionStore()?.setItem(GROUP_REJOIN_KEY, JSON.stringify({
            callId: call.callId,
            callerId: call.clientId,
            leftAt: now,
            roomName: call.roomName
        }));
    }
    catch {
        // Storage can be blocked; a reload then asks to be let back in by hand.
    }
}
/**
 * Forget the marker once the rejoin is settled either way (a timeout keeps
 * it, so reloading again retries).
 *
 * @returns {void}
 */
export function clearGroupRejoin() {
    sessionStore()?.removeItem(GROUP_REJOIN_KEY);
}
/**
 * Whether this page is waiting to be seated again in that call: its
 * p2p_join_room is the answer, not an invitation to navigate somewhere.
 *
 * @param {string} [callId] - The call in question; any pending rejoin if omitted.
 * @returns {boolean}
 */
export function isGroupRejoinPending(callId) {
    return Boolean(pendingRejoinCallId) && (callId === undefined || callId === pendingRejoinCallId);
}
/**
 * Ask to be seated again and wait for the answer.
 *
 * @param {IRejoinService} service - The queue service.
 * @param {IGroupRejoin} rejoin - The call to rejoin.
 * @param {number} timeoutMs - How long to wait.
 * @returns {Promise<GroupRejoinOutcome>}
 */
export function requestGroupRejoin(service, rejoin, timeoutMs = GROUP_REJOIN_TIMEOUT_MS, { onWaiting } = {}) {
    return new Promise(resolve => {
        let settled = false;
        const finish = (outcome) => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timer);
            service.off('p2pJoinRoom', onJoin);
            service.off('error', onError);
            service.off('groupJoinWaiting', onHostAsked);
            pendingRejoinCallId = undefined;
            if (outcome.ok || outcome.code !== 'TIMEOUT') {
                clearGroupRejoin();
            }
            resolve(outcome);
        };
        const onJoin = (data) => {
            if (data?.callId === rejoin.callId) {
                finish({ ok: true });
            }
        };
        const onError = (data) => {
            if (!data?.operation && REJOIN_REFUSALS.includes(String(data?.code || ''))) {
                finish({ code: String(data.code), ok: false });
            }
        };
        let timer = setTimeout(() => finish({ code: 'TIMEOUT', ok: false }), timeoutMs);
        // The host is asked (no active ring): wait for their answer, which
        // the server times (vrs-server/ws/group-join-approval.js).
        const onHostAsked = (data) => {
            if (settled || data?.callId !== rejoin.callId) {
                return;
            }
            clearTimeout(timer);
            timer = setTimeout(() => finish({ code: 'TIMEOUT', ok: false }), (typeof data.timeoutMs === 'number' ? data.timeoutMs : 30000) + HOST_ANSWER_MARGIN_MS);
            onWaiting?.();
        };
        pendingRejoinCallId = rejoin.callId;
        service.on('p2pJoinRoom', onJoin);
        service.on('error', onError);
        service.on('groupJoinWaiting', onHostAsked);
        try {
            service.acceptP2PCall({ callId: rejoin.callId, callerId: rejoin.callerId, roomName: rejoin.roomName });
        }
        catch {
            finish({ code: 'TIMEOUT', ok: false });
        }
    });
}
/**
 * Why Join or Rejoin on Home (web client-profile.js startRejoin, native VRS
 * Home) did not open the call, in the person's terms. Unlike a reloaded
 * room, Home has nothing to reload: it asks to try again.
 *
 * @param {string} code - The refusal code, or TIMEOUT.
 * @returns {string}
 */
export function groupJoinFailureMessage(code) {
    switch (code) {
        case 'JOIN_DECLINED':
            return 'The host declined.';
        case 'JOIN_NO_ANSWER':
            return 'The host did not answer.';
        case 'CALL_FULL':
            return 'This call is full — 8 people max.';
        case 'CALL_ENDED':
        case 'NOT_FOUND':
            return 'This call has ended.';
        case 'TIMEOUT':
            return 'Could not join the call. Try again.';
        default:
            return 'You can no longer join this call.';
    }
}
/**
 * Why the room did not open again, in the person's terms.
 *
 * @param {string} code - The refusal code.
 * @returns {string}
 */
export function groupRejoinFailureMessage(code) {
    switch (code) {
        case 'JOIN_DECLINED':
            return 'The host declined.';
        case 'JOIN_NO_ANSWER':
            return 'The host did not answer.';
        case 'CALL_FULL':
            return 'This call is full — 8 people max.';
        case 'CALL_ENDED':
        case 'NOT_FOUND':
            return 'This call has ended.';
        case 'TIMEOUT':
            return 'Could not reconnect. Reload.';
        default:
            return 'You can no longer rejoin this call.';
    }
}
